import { useContext } from 'react';
import { EntitlementContext, type EntitlementContextValue, type QuotaInfo } from '../contexts/entitlementContext';

/**
 * Main hook to access entitlement context.
 * Use this for migrated entitlement surfaces.
 * The existing useFeatures() hook remains available for surfaces that have not migrated yet.
 *
 * @userstory US-ENT-01, US-ENT-02, US-ENT-03
 */
export const useEntitlements = (): EntitlementContextValue => {
	const context = useContext(EntitlementContext);
	if (!context) {
		throw new Error('useEntitlements must be used within an EntitlementProvider');
	}
	return context;
};

/**
 * Convenience hook: returns the quota info for a single key.
 * Returns null if quota has not been loaded yet.
 *
 * @userstory US-ENT-02, US-CPF-SRCH-07, US-CPF-SRCH-08
 */
export const useQuota = (key: string): QuotaInfo | null => {
	const { getQuota } = useEntitlements();
	return getQuota(key);
};

/**
 * Convenience hook: returns true if the org's plan includes the capability.
 *
 * @userstory US-ENT-01
 */
export const useCapability = (key: string): boolean => {
	const { hasCapability } = useEntitlements();
	return hasCapability(key);
};

/**
 * Convenience hook: returns true if the org has the add-on active.
 *
 * @userstory US-ENT-03
 */
export const useAddOn = (key: string): boolean => {
	const { hasAddOn } = useEntitlements();
	return hasAddOn(key);
};
